import React, { useMemo } from 'react'
import { useSelector } from '@xstate/react'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import { sortBy } from 'lib/array/arrayUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import Clickable from 'components/Common/Clickable'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import { getCartMachineActor } from 'machines/cart/MultiCartMachineProvider'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import SharedCartsUserAvatarGroup from '../SharedCartsUserAvatarGroup'
import { pluralizeToString } from 'lib/string/pluralize'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalBody from 'LuxKit/Modal/ModalBody'

const StyledOptionContainer = styled(Clickable)`
  padding: ${rem(8)};
  width: 100%;

  ${mediaHoverable} {
    &:hover {
      cursor: pointer;
      background-color: ${props => props.theme.palette.neutral.default.seven};
    }
  }

  &:focus {
    ${interactiveFocusStyle}
  }
`

const StyledIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(48)};
  height: ${rem(48)};
  background-color: ${props => props.theme.palette.neutral.default.six};

  &.is-create {
    border: 1px solid ${props => props.theme.palette.neutral.default.five};
    background-color: ${props => props.theme.palette.neutral.default.eight};
  }
`

type Props = {
  multiCartItemIdsToMove: Array<string>
  onCreateNewSharedCart: (step: 'cartSelection' | 'createSharedCart') => void
  onClose: () => void
}

function SharedCartsCartSelectionModalContent(props: Props) {
  const { multiCartItemIdsToMove, onCreateNewSharedCart, onClose } = props

  // This modal is getting opened from Snackbar which is out of context of MachineContextProvider.
  // hence we use getCartMachineActor() to get the actor ref else MultiCartContext.useActorRef() is preferred.
  const onlyChangeCart = multiCartItemIdsToMove.length === 0

  const multiCartActorRef = getCartMachineActor()
  if (!multiCartActorRef) {
    throw new Error('Multi cart actor ref not found')
  }
  const cartSummary = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartSummary)
  const cartItems = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartItems)
  const multiCartsArray = useMemo(() => cartSummary.status === 'loaded' ? Object.values(cartSummary.data) : [], [cartSummary])
  const sortedMultiCarts = useMemo(() => sortBy(multiCartsArray, (cart) => cart.type === 'default' ? 0 : 1, 'asc'), [multiCartsArray])
  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)

  const handleCreateNewSharedCart = () => {
    onCreateNewSharedCart('createSharedCart')
  }

  const handleAddItemsToSharedCart = (cartId: string) => {
    multiCartActorRef.send({
      type: 'select and move items to target cart',
      targetCartId: cartId,
      multiCartItemIdsToMove: multiCartItemIdsToMove,
    })

    onClose()
  }

  const handleActiveCartChange = (cartId: string) => {
    multiCartActorRef.send({
      type: 'set active cart',
      cartId,
    })
    onClose()
  }

  return (
    <>
      <ModalHeader title={onlyChangeCart ? 'Change cart' : 'Add to cart'} onCloseButtonClick={onClose} />
      <ModalBody>
        <ModalContent>
          <Group direction="vertical" gap={20}>
            <BodyText variant="large">{onlyChangeCart ? 'Choose which cart you would like to view.' : 'Choose which cart you would like to add this item to.'}</BodyText>
            <CSSBreakpoint min="tablet">
              {!onlyChangeCart && <StyledOptionContainer onClick={handleCreateNewSharedCart}>
                <Group direction="horizontal" gap={16} verticalAlign="center">
                  <StyledIconContainer className="is-create">
                    <LinePlusIcon />
                  </StyledIconContainer>
                  <BodyText variant="large">Create new shared cart</BodyText>
                </Group>
              </StyledOptionContainer>}
            </CSSBreakpoint>
            {sortedMultiCarts.filter(cart => cart.cartId !== activeCartId).map(cart => (
              <StyledOptionContainer key={cart.cartId} onClick={() => onlyChangeCart ? handleActiveCartChange(cart.cartId) : handleAddItemsToSharedCart(cart.cartId)}>
                <Group direction="horizontal" gap={16} verticalAlign="center" horizontalAlign="space-between">
                  <Group direction="horizontal" gap={16} verticalAlign="center">
                    <StyledIconContainer>
                      {cart.type === 'default' ? <LineUserIcon colour="neutral-three" /> : <LineUsersAltIcon colour="neutral-three" />}
                    </StyledIconContainer>
                    <div>
                      <BodyText variant="large">{cart.type === 'default' ? 'My cart' : cart.name}</BodyText>
                      {cartItems.status === 'loaded' && <BodyText variant="medium" colour="neutral-three">
                        {pluralizeToString('item', Object.keys(cartItems.data[cart.cartId]).length)} added
                      </BodyText>}
                    </div>
                  </Group>
                  <SharedCartsUserAvatarGroup activeCart={cart} />
                </Group>
              </StyledOptionContainer>
            ))}
            <CSSBreakpoint max="mobile">
              {!onlyChangeCart && <StyledOptionContainer onClick={handleCreateNewSharedCart}>
                <Group direction="horizontal" gap={16} verticalAlign="center">
                  <StyledIconContainer className="is-create">
                    <LinePlusIcon />
                  </StyledIconContainer>
                  <BodyText variant="large">Create new shared cart</BodyText>
                </Group>
              </StyledOptionContainer>}
            </CSSBreakpoint>
          </Group>
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default SharedCartsCartSelectionModalContent
